import React, { useEffect, useRef, useState } from 'react';
import { ClipboardCheck, MapPin, Search, X } from 'lucide-react';
import clsx from 'clsx';
import { classifySearchInput, shouldCommitSearchInput } from '../../utils/addressSearch';

const TYPES = ['', 'detached', 'semi-detached', 'terraced', 'flat'];
const SORTS = [
  { value: 'investment_score', label: 'Score' },
  { value: 'asking_price', label: 'Price' },
  { value: 'yield', label: 'Yield' },
  { value: 'date_found', label: 'Date Found' },
];
const RADIUS_OPTIONS = [5, 10, 20, 50];

export default function PropertyFilters({
  filters,
  onChange,
  onReset,
  disabled = false,
  disabledHint,
  onDueDiligence,
  dueDiligenceEnabled = false,
}) {
  const [sources, setSources] = useState([]);
  const [postcodeInput, setPostcodeInput] = useState('');
  const [radiusInput, setRadiusInput] = useState('');
  const postcodeInputRef = useRef(null);
  const radiusInputRef = useRef(null);
  const handleChange = (key, value) => onChange({ ...filters, [key]: value || undefined, page: 1 });

  const isAddressMode = !!filters.address_query;
  const isRadiusMode = !!(filters.center_postcode);

  // Parse comma-separated postcodes from filters into an array of tags
  const postcodeTags = filters.postcode
    ? filters.postcode.split(',').map(p => p.trim().toUpperCase()).filter(Boolean)
    : [];

  const applyAddressSearch = (raw) => {
    const q = raw.trim();
    if (!q) return;
    onChange({
      ...filters,
      address_query: q,
      postcode: undefined,
      center_postcode: undefined,
      radius_miles: undefined,
      source: undefined,
      page: 1,
    });
    setPostcodeInput('');
  };

  const clearAddressSearch = () => {
    onChange({ ...filters, address_query: undefined, page: 1 });
  };

  const addPostcodeTag = (raw) => {
    const tag = raw.trim().toUpperCase();
    if (!tag) return;
    const next = [...new Set([...postcodeTags, tag])];
    onChange({
      ...filters,
      postcode: next.join(',') || undefined,
      address_query: undefined,
      center_postcode: undefined,
      radius_miles: undefined,
      page: 1,
    });
    setPostcodeInput('');
  };

  const commitSearchInput = (raw) => {
    const text = raw.trim();
    if (!text) return;
    if (classifySearchInput(text) === 'address') {
      applyAddressSearch(text);
    } else {
      addPostcodeTag(text);
    }
  };

  const removePostcodeTag = (tag) => {
    const next = postcodeTags.filter(t => t !== tag);
    onChange({ ...filters, postcode: next.length ? next.join(',') : undefined, page: 1 });
  };

  const handlePostcodeKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (shouldCommitSearchInput(postcodeInput)) commitSearchInput(postcodeInput);
    } else if (e.key === ',') {
      if (classifySearchInput(postcodeInput) === 'postcode') {
        e.preventDefault();
        commitSearchInput(postcodeInput);
      }
    } else if (e.key === 'Backspace' && !postcodeInput && postcodeTags.length > 0) {
      removePostcodeTag(postcodeTags[postcodeTags.length - 1]);
    }
  };

  const handleSearchInputChange = (value) => {
    if (classifySearchInput(value) === 'postcode' && !value.includes(',')) {
      setPostcodeInput(value.toUpperCase());
    } else {
      setPostcodeInput(value);
    }
  };

  // Radius mode helpers
  const applyRadius = (postcode, miles) => {
    const pc = postcode.trim().toUpperCase();
    if (!pc) return;
    onChange({
      ...filters,
      center_postcode: pc,
      radius_miles: miles || 20,
      postcode: undefined,
      address_query: undefined,
      page: 1,
    });
  };

  const clearRadius = () => {
    setRadiusInput('');
    onChange({ ...filters, center_postcode: undefined, radius_miles: undefined, page: 1 });
  };

  const handleRadiusKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      applyRadius(radiusInput, filters.radius_miles || 20);
    }
  };

  useEffect(() => {
    if (disabled) {
      setSources([]);
      return;
    }
    const token = localStorage.getItem('assetlens_token');
    fetch('/api/scrapers', token ? { headers: { Authorization: `Bearer ${token}` } } : {})
      .then(r => r.ok ? r.json() : [])
      .then(d => setSources(Array.isArray(d) ? d : []))
      .catch(() => setSources([]));
  }, [disabled]);

  const advancedClass = disabled
    ? 'opacity-50 pointer-events-none cursor-not-allowed select-none'
    : '';

  const blockInteraction = (e) => {
    if (disabled) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  return (
    <div
      className={clsx(
        'bg-slate-900 border border-slate-800 rounded-2xl p-4 space-y-3',
        disabled && 'opacity-60',
      )}
      onClickCapture={blockInteraction}
      onKeyDownCapture={blockInteraction}
    >
      <div className={clsx('flex items-center gap-3 flex-wrap', advancedClass)}>
        {/* Address chip */}
        {isAddressMode ? (
          <>
            <div className="flex items-center gap-1.5 flex-1 min-w-[200px] bg-blue-500/15 text-blue-200 text-sm font-medium px-3 py-2 rounded-xl border border-blue-500/30">
              <Search size={14} className="shrink-0" />
              <span className="truncate">{filters.address_query}</span>
              <button type="button" onClick={clearAddressSearch} className="ml-auto shrink-0 hover:text-white">
                <X size={14} />
              </button>
            </div>
            {onDueDiligence && (
              <button
                type="button"
                onClick={onDueDiligence}
                disabled={!dueDiligenceEnabled}
                title={dueDiligenceEnabled ? 'Analyse this off-market address' : 'Available on Basic or Pro plans'}
                className={`shrink-0 px-3 py-2.5 rounded-xl text-sm font-semibold inline-flex items-center gap-2 transition-colors ${
                  dueDiligenceEnabled
                    ? 'bg-violet-600 hover:bg-violet-500 text-white'
                    : 'bg-slate-800 text-slate-500 cursor-not-allowed opacity-60'
                }`}
              >
                <ClipboardCheck size={15} />
                Due diligence
              </button>
            )}
          </>
        ) : isRadiusMode ? (
          /* Active radius chip */
          <div className="flex items-center gap-1.5 bg-emerald-500/20 text-emerald-300 text-xs font-medium px-3 py-1.5 rounded-xl border border-emerald-500/30">
            <MapPin size={13} className="shrink-0" />
            <span>Within {filters.radius_miles ?? 20} mi of {filters.center_postcode}</span>
            <button type="button" onClick={clearRadius} className="ml-1 hover:text-white">
              <X size={11} />
            </button>
          </div>
        ) : (
          /* Multi-postcode chip input or full address */
          <div
            className="relative flex items-center flex-wrap gap-1.5 flex-1 min-w-[200px] bg-slate-800 border border-slate-700 rounded-xl px-2.5 py-1.5 cursor-text focus-within:border-emerald-500"
            onClick={() => postcodeInputRef.current?.focus()}
          >
            <Search size={14} className="text-slate-500 shrink-0" />
            {postcodeTags.map(tag => (
              <span key={tag} className="flex items-center gap-1 bg-emerald-500/20 text-emerald-300 text-xs font-medium px-2 py-0.5 rounded-lg">
                {tag}
                <button type="button" onClick={e => { e.stopPropagation(); removePostcodeTag(tag); }} className="hover:text-white">
                  <X size={11} />
                </button>
              </span>
            ))}
            <input
              ref={postcodeInputRef}
              type="text"
              disabled={disabled}
              readOnly={disabled}
              placeholder={postcodeTags.length === 0 ? 'Postcode (WD25) or full address…' : ''}
              value={postcodeInput}
              onChange={e => handleSearchInputChange(e.target.value)}
              onKeyDown={handlePostcodeKeyDown}
              onBlur={() => {
                if (shouldCommitSearchInput(postcodeInput)) commitSearchInput(postcodeInput);
              }}
              className="flex-1 min-w-[120px] bg-transparent text-sm text-slate-200 placeholder-slate-500 focus:outline-none py-0.5"
            />
          </div>
        )}

        {/* Radius search — hidden during address lookup */}
        {!isRadiusMode && !isAddressMode && (
          <div className={`flex items-center gap-1.5 bg-slate-800 border border-slate-700 rounded-xl px-2.5 py-1.5 focus-within:border-emerald-500 ${advancedClass}`}>
            <MapPin size={14} className="text-slate-500 shrink-0" />
            <select
              value={filters.radius_miles ?? 20}
              onChange={e => onChange({ ...filters, radius_miles: Number(e.target.value), page: 1 })}
              className="bg-transparent text-xs text-slate-300 focus:outline-none"
            >
              {RADIUS_OPTIONS.map(r => (
                <option key={r} value={r}>{r} mi</option>
              ))}
            </select>
            <input
              ref={radiusInputRef}
              type="text"
              placeholder="Center postcode…"
              value={radiusInput}
              onChange={e => setRadiusInput(e.target.value.toUpperCase())}
              onKeyDown={handleRadiusKeyDown}
              onBlur={() => radiusInput.trim() && applyRadius(radiusInput, filters.radius_miles || 20)}
              className="w-28 bg-transparent text-sm text-slate-200 placeholder-slate-500 focus:outline-none"
            />
          </div>
        )}

        {/* Source — disabled during address lookup */}
        <select
          value={filters.source || ''}
          disabled={isAddressMode || disabled}
          title={disabled ? disabledHint : isAddressMode ? 'Source filter disabled for address search' : undefined}
          onChange={e => handleChange('source', e.target.value)}
          className={[
            'bg-slate-800 border border-slate-700 rounded-xl px-3 py-2.5 text-sm text-slate-200 focus:outline-none focus:border-emerald-500',
            (isAddressMode || disabled) ? 'opacity-40 cursor-not-allowed' : '',
          ].join(' ')}
        >
          <option value="">All Sources</option>
          {sources.map(s => (
            <option key={s.id} value={s.name}>{s.name}</option>
          ))}
        </select>

        {/* Property type */}
        <select
          value={filters.property_type || ''}
          disabled={disabled}
          onChange={e => handleChange('property_type', e.target.value)}
          className={`bg-slate-800 border border-slate-700 rounded-xl px-3 py-2.5 text-sm text-slate-200 focus:outline-none focus:border-emerald-500 ${advancedClass}`}
        >
          <option value="">All Types</option>
          {TYPES.filter(Boolean).map(t => (
            <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
          ))}
        </select>

        {/* Sort */}
        <select
          value={filters.sort_by || 'investment_score'}
          disabled={disabled}
          onChange={e => handleChange('sort_by', e.target.value)}
          className={`bg-slate-800 border border-slate-700 rounded-xl px-3 py-2.5 text-sm text-slate-200 focus:outline-none focus:border-emerald-500 ${advancedClass}`}
        >
          {SORTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>

        <button
          onClick={onReset}
          disabled={disabled}
          className={`p-2.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-xl transition-colors ${advancedClass}`}
          title="Reset filters"
        >
          <X size={16} />
        </button>
      </div>

      {isAddressMode && (
        <p className="text-xs text-slate-500">
          Address search is active — radius and source filters are disabled so we can match the property across all listings.
        </p>
      )}

      {/* Range filters */}
      <div className={`flex items-center gap-3 flex-wrap ${advancedClass}`}>
        <div className="flex items-center gap-2">
          <span className="text-slate-500 text-xs whitespace-nowrap">Min Score</span>
          <input
            type="number" min="0" max="100" placeholder="0"
            value={filters.min_score || ''}
            onChange={e => handleChange('min_score', e.target.value ? Number(e.target.value) : undefined)}
            className="w-20 bg-slate-800 border border-slate-700 rounded-lg px-2 py-1.5 text-sm text-slate-200 focus:outline-none focus:border-emerald-500"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-slate-500 text-xs whitespace-nowrap">Min Yield %</span>
          <input
            type="number" min="0" max="20" step="0.5" placeholder="0"
            value={filters.min_yield || ''}
            onChange={e => handleChange('min_yield', e.target.value ? Number(e.target.value) : undefined)}
            className="w-20 bg-slate-800 border border-slate-700 rounded-lg px-2 py-1.5 text-sm text-slate-200 focus:outline-none focus:border-emerald-500"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-slate-500 text-xs whitespace-nowrap">Beds</span>
          <input
            type="number" min="1" max="10" placeholder="Min"
            value={filters.min_beds || ''}
            onChange={e => handleChange('min_beds', e.target.value ? Number(e.target.value) : undefined)}
            className="w-16 bg-slate-800 border border-slate-700 rounded-lg px-2 py-1.5 text-sm text-slate-200 focus:outline-none focus:border-emerald-500"
          />
          <span className="text-slate-600 text-xs">—</span>
          <input
            type="number" min="1" max="10" placeholder="Max"
            value={filters.max_beds || ''}
            onChange={e => handleChange('max_beds', e.target.value ? Number(e.target.value) : undefined)}
            className="w-16 bg-slate-800 border border-slate-700 rounded-lg px-2 py-1.5 text-sm text-slate-200 focus:outline-none focus:border-emerald-500"
          />
        </div>
      </div>

      {disabled && disabledHint && (
        <p className="text-xs text-slate-500">{disabledHint}</p>
      )}
    </div>
  );
}
